import { Badge, useBreakpointValue } from "@chakra-ui/react";
import { useState, useEffect } from 'react';

export const isAuthenticated = token => !!token;

export const saveSites = token => localStorage.setItem('user_sites', token);

export const getSites = () => localStorage.getItem('user_sites');

export const removeSites = () => localStorage.removeItem('user_sites');


export const saveSiteConfig = config => setItemWithCheck('site_config', config);

const isPublicRoute = () => window.location.hash.startsWith('#/public/');

export const getSiteConfig = () => {
  const config = localStorage.getItem('site_config');

  // Check for invalid config values
  if (!config || config === 'null' || config === 'undefined' || config === '') {
    if (isPublicRoute()) return null; // Rutas públicas no requieren site_config
    localStorage.clear();
    window.location.href = '/#/auth/signin';
    return null;
  }

  try {
    // Validate if config is valid JSON
    const parsedConfig = JSON.parse(config);
    if (!parsedConfig) {
      if (isPublicRoute()) return null;
      localStorage.clear();
      window.location.href = '/#/auth/signin';
      return null;
    }
    return config;
  } catch (error) {
    if (isPublicRoute()) return null;
    localStorage.clear();
    window.location.href = '/#/auth/signin';
    return null;
  }
};

export function setItemWithCheck(key, value) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
}

export const saveCustomerSelected = customer => setItemWithCheck('customer_selected', customer);

export const getCustomerSelected = () => localStorage.getItem('customer_selected');

export const transformarFecha = (fechaString) => {
  const fecha = new Date(fechaString);
  
  const opciones = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  };

  const fechaFormateada = new Intl.DateTimeFormat('es-ES', opciones).format(fecha);

  return fechaFormateada;
}

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const formatCurrency = (amount) => {
return new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP'
}).format(amount);
};


// utils/site.js
export const StatusBadge = ({ status }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const statusConfig = {
    completed: { color: 'green', text: 'Pagado' },
    pending: { color: 'yellow', text: 'Pendiente' },
    failed: { color: 'red', text: 'Fallido' },
    used: { color: 'blue', text: 'Escaneado' },
    default: { color: 'gray', text: 'Desconocido' }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Badge 
      colorScheme={config.color}
      size={isMobile ? 'sm' : 'lg'}
      px={isMobile ? 3 : 4}
      py={isMobile ? 2 : 2}
      borderRadius="full"
    >
      {config.text}
    </Badge>
  );
};

export function getStatusColor(status) {
  let color;

  switch (status) {
    case "pending":
      color = "yellow";
      break;
    case "completed":
      color = "green";
      break;
    case "failed":
      color = "red";
      break;
    case "used":
      color = "blue";
      break;
    default:
      color = "gray";
      break;
  }

  return color;
}

// Esta función formatea los errores en un mensaje adecuado para mostrar en la interfaz de usuario
export const formatErrors = (message, errors) => {
  const errorMessages = [];

  if (errors && typeof errors === 'object') {
    for (const key in errors) {
      if (errors.hasOwnProperty(key)) {
        errorMessages.push(...errors[key]);
      }
    }
  }

  return {
    message,
    errorMessages, // Array de mensajes de error
  };
};



// Verifica si un módulo está activo para el sitio actual
export const isModuleEnabled = (siteConfig, moduleSlug) => {
  if (!siteConfig) return false;
  
  // Verificar si modules es un array directamente
  if (Array.isArray(siteConfig.modules)) {
    return siteConfig.modules.some(module => module.slug === moduleSlug);
  }
  
  // Verificar si está en siteConfig directamente (estructura alternativa)
  if (Array.isArray(siteConfig)) {
    return siteConfig.some(module => module.slug === moduleSlug);
  }
  
  // Verificar si está en siteConfig.data.modules (otra estructura posible)
  if (siteConfig.data && Array.isArray(siteConfig.data.modules)) {
    return siteConfig.data.modules.some(module => module.slug === moduleSlug);
  }
  
  return false;
};

// Verifica si una encuesta está disponible para el sitio
export const isSurveyEnabled = (siteConfig, surveySlug) => {
  if (!siteConfig || !siteConfig.surveys) return false;
  return siteConfig.surveys.some(survey => survey.slug === surveySlug);
};

// Obtiene el valor de una configuración específica
export const getConfigValue = (siteConfig, key) => {
  if (!siteConfig || !siteConfig.configs) return null;
  const config = siteConfig.configs.find(config => config.key === key);
  return config ? config.value : null;
};

// Componente de alto orden para renderizado condicional basado en módulo
export const withModuleCheck = (WrappedComponent, moduleSlug) => {
  return function WithModuleCheck(props) {
    const siteConfig = JSON.parse(getSiteConfig() || '{}');
    if (!isModuleEnabled(siteConfig, moduleSlug)) {
      return null;
    }
    return <WrappedComponent {...props} />;
  };
};

// Hook personalizado para verificar módulos
export const useModuleCheck = (moduleSlug) => {
  // Debug logging 
  if (moduleSlug === 'agenda') {
  }
  
  // Intentar obtener desde getSiteConfig() primero
  const siteConfigString = getSiteConfig();
  let siteConfig = null;
  
  if (siteConfigString) {
    try {
      siteConfig = JSON.parse(siteConfigString);
    } catch (error) {
    }
  }
  
  // Si no funciona, obtener directamente desde localStorage
  if (!siteConfig) {
    try {
      const directConfig = localStorage.getItem('site_config');
      if (directConfig) {
        siteConfig = JSON.parse(directConfig);
      }
    } catch (error) {
    }
  }
  
  if (moduleSlug === 'agenda') {
  }
  
  if (!siteConfig) {
    return false;
  }
  
  const result = isModuleEnabled(siteConfig, moduleSlug);
  
  if (moduleSlug === 'agenda') {
  }
  
  return result;
};

// Definición de módulos disponibles - constante fuera del componente
export const MODULES = {
  PAYMENTS: 'payments',
  TICKETS: 'tickets',
  SURVEYS: 'surveys',
  FILES: 'files',
  SUBSCRIPTIONS: 'subscriptions',
  AGENDA: 'agenda',
  SISTEMA: 'sistema',
  ACCESS_CONTROL: 'access_control',
};

// Categorías de Surveys - organizan los formularios por tipo (glosario completo)
export const SURVEY_CATEGORIES = {
  'Matriculas': '📋 Formularios de Matrícula',
  'matriculas': '📋 Formularios de Matrícula',
  'Matricula': '📋 Formularios de Matrícula',
  'matricula': '📋 Formularios de Matrícula',
  'MATRICULAS': '📋 Formularios de Matrícula',
  
  'Salud': '🏥 Formularios de Salud', 
  'salud': '🏥 Formularios de Salud',
  'SALUD': '🏥 Formularios de Salud',
  
  'Atencion': '👨‍🏫 Formularios de Atención',
  'atencion': '👨‍🏫 Formularios de Atención',
  'Atención': '👨‍🏫 Formularios de Atención',
  'atención': '👨‍🏫 Formularios de Atención',
  'ATENCION': '👨‍🏫 Formularios de Atención',
  'ATENCIÓN': '👨‍🏫 Formularios de Atención',
  
  'Documentos': '📄 Documentación',
  'documentos': '📄 Documentación',
  'Documento': '📄 Documentación',
  'documento': '📄 Documentación',
  'DOCUMENTOS': '📄 Documentación',
  
  'Becas': '🎓 Becas y Ayudas',
  'becas': '🎓 Becas y Ayudas',
  'Beca': '🎓 Becas y Ayudas',
  'beca': '🎓 Becas y Ayudas',
  'BECAS': '🎓 Becas y Ayudas',
  
  'Casos': '⚠️ Casos Especiales',
  'casos': '⚠️ Casos Especiales',
  'Caso': '⚠️ Casos Especiales',
  'caso': '⚠️ Casos Especiales',
  'CASOS': '⚠️ Casos Especiales',
  
  'otros': '📂 Otros Formularios',
  'Otros': '📂 Otros Formularios',
  'OTROS': '📂 Otros Formularios'
};

// Función helper para normalizar categorías
export const normalizeCategoryName = (category) => {
  if (!category) return 'otros';
  
  // Mapeo de normalización para obtener la clave canónica
  const normalizeMap = {
    'Matriculas': 'Matriculas',
    'matriculas': 'Matriculas', 
    'Matricula': 'Matriculas',
    'matricula': 'Matriculas',
    'MATRICULAS': 'Matriculas',
    
    'Salud': 'Salud',
    'salud': 'Salud',
    'SALUD': 'Salud',
    
    'Atencion': 'Atencion',
    'atencion': 'Atencion',
    'Atención': 'Atencion',
    'atención': 'Atencion',
    'ATENCION': 'Atencion',
    'ATENCIÓN': 'Atencion',
    
    'Documentos': 'Documentos',
    'documentos': 'Documentos',
    'Documento': 'Documentos', 
    'documento': 'Documentos',
    'DOCUMENTOS': 'Documentos',
    
    'Becas': 'Becas',
    'becas': 'Becas',
    'Beca': 'Becas',
    'beca': 'Becas', 
    'BECAS': 'Becas',
    
    'Casos': 'Casos',
    'casos': 'Casos',
    'Caso': 'Casos',
    'caso': 'Casos',
    'CASOS': 'Casos'
  };
  
  return normalizeMap[category] || 'otros';
};

// Función para obtener el nombre displayable de una categoría.
// Usa la DB (surveyCategoriesService) si ya cargó; si no, cae al mapa hardcodeado.
export const getCategoryDisplayName = (category) => {
  try {
    const { getLabelForCategory } = require('../services/surveyCategoriesService');
    const label = getLabelForCategory(category);
    if (label && label !== category) return label;
  } catch {}
  const normalized = normalizeCategoryName(category);
  return SURVEY_CATEGORIES[normalized] || SURVEY_CATEGORIES['otros'];
};

// Etiquetas de Survey Responses - clasifican respuestas individuales
export const RESPONSE_TAGS = {
  'casos': '⚠️ Casos Especiales',
  'reservado': '🔒 Información Reservada',
  'medida-disciplinaria': '🚫 Medida Disciplinaria', 
  'normal': '📝 Respuesta Normal'
};

// Control de acceso por rol - qué etiquetas puede ver cada rol
export const ROLE_TAG_ACCESS = {
  'administrador': ['casos', 'reservado', 'medida-disciplinaria', 'normal'],
  'profesor': ['normal'] // Solo respuestas normales, excluye sensibles
};

// Acceso por rol — fuente de verdad: survey_categories.role_access en la DB.
// La tabla se gestiona desde Sistema → Categorías y se carga vía surveyCategoriesService.
// ROLE_CATEGORY_ACCESS se mantiene solo como referencia de los valores por defecto.
export const ROLE_CATEGORY_ACCESS = {
  'administrador': ['matriculas', 'salud', 'atencion', 'documentos', 'becas', 'casos', 'otros'],
  'profesor':      ['matriculas', 'salud', 'atencion', 'documentos', 'otros'],
};

// Función helper para verificar si un rol puede ver una categoría específica.
// Usa el caché de surveyCategoriesService; cae al fallback hardcodeado si aún no cargó.
export const canUserAccessCategory = (userRole, category) => {
  try {
    const { canView } = require('../services/surveyCategoriesService');
    // OJO: no pasar `category` por normalizeCategoryName() acá — ese mapa solo conoce
    // las 6 categorías viejas (matriculas/salud/atencion/documentos/becas/casos) y
    // cualquier slug real que no esté ahí (ej. 'doc', 'inspectoria', 'retiros',
    // 'salidas-pedagogicas', 'salidas-enfermedad') caía silenciosamente a 'otros',
    // heredando el permiso de 'otros' en vez del propio. canView() ya hace su propio
    // match case-insensitive contra las categorías reales de la DB.
    return canView(userRole, category || 'otros');
  } catch {
    // fallback si el servicio no está disponible
    const allowed = ROLE_CATEGORY_ACCESS[userRole] || ROLE_CATEGORY_ACCESS['profesor'];
    return allowed.includes(normalizeCategoryName(category).toLowerCase());
  }
};

// Función helper para filtrar categorías por rol
export const filterCategoriesByRole = (categories, userRole) => {
  return categories.filter(category => canUserAccessCategory(userRole, category));
};

// Función helper para obtener color de etiqueta
export const getTagColor = (tag) => {
  const colors = {
    'casos': 'red',
    'reservado': 'purple', 
    'medida-disciplinaria': 'orange',
    'normal': 'green'
  };
  return colors[tag] || 'gray';
};


// Hook para obtener el usuario autenticado desde datofy-api (con cache optimizado)
export const useUserAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        // Importar getUserAuth dinámicamente para evitar dependencias circulares
        const { getUserAuth } = await import('../services/surveyService');
        // getUserAuth ahora usa cache interno - solo hará API call si es necesario
        const userData = await getUserAuth();
        setUser(userData);
        setError(null);
      } catch (err) {
        setError(err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []); // Sin dependencias adicionales para evitar re-llamadas

  return { user, loading, error };
};

// Módulos internos que no requieren estar en site_config.modules
// (siempre disponibles, acceso controlado solo por rol)
const INTERNAL_MODULES = [MODULES.SISTEMA, MODULES.SURVEYS, MODULES.ACCESS_CONTROL];

// Hook híbrido que combina módulo habilitado + rol de usuario
export const useRoleModuleAccess = (moduleSlug) => {
  const moduleEnabled = useModuleCheck(moduleSlug);
  const { user } = useUserAuth();

  const isInternal = INTERNAL_MODULES.includes(moduleSlug);
  if (!isInternal && !moduleEnabled) return false;
  if (!user?.role) return false;
  
  // Matriz de acceso por rol a módulos
  const moduleAccess = {
    'administrador': {
      [MODULES.PAYMENTS]: true,
      [MODULES.TICKETS]: true,
      [MODULES.SURVEYS]: true,
      [MODULES.FILES]: true,
      [MODULES.SUBSCRIPTIONS]: true,
      [MODULES.AGENDA]: true,
      [MODULES.SISTEMA]: true,
      [MODULES.ACCESS_CONTROL]: true,
    },
    'profesor': {
      [MODULES.PAYMENTS]: false,
      [MODULES.TICKETS]: false,
      [MODULES.SURVEYS]: true,
      [MODULES.FILES]: true,
      [MODULES.SUBSCRIPTIONS]: true,
      [MODULES.AGENDA]: true,
      [MODULES.SISTEMA]: false,
      [MODULES.ACCESS_CONTROL]: true,
    },
    'teacher': {
      [MODULES.PAYMENTS]: false,
      [MODULES.TICKETS]: false,
      [MODULES.SURVEYS]: true,
      [MODULES.FILES]: true,
      [MODULES.SUBSCRIPTIONS]: true,
      [MODULES.AGENDA]: true,
      [MODULES.SISTEMA]: false,
      [MODULES.ACCESS_CONTROL]: true,
    },
    // Inspector: solo alumnos y accesos QR (sin pagos, fichas, agenda ni sistema)
    'inspector': {
      [MODULES.PAYMENTS]: false,
      [MODULES.TICKETS]: false,
      [MODULES.SURVEYS]: false,
      [MODULES.FILES]: false,
      [MODULES.SUBSCRIPTIONS]: true,
      [MODULES.AGENDA]: false,
      [MODULES.SISTEMA]: false,
      [MODULES.ACCESS_CONTROL]: true,
    },
  };
  
  return moduleAccess[user.role]?.[moduleSlug] || false;
};

// Quién puede *abrir* el escáner (lector USB/cámara/manual) — administrador
// e inspector. Distinto de poder ver la página "Accesos QR" (ACCESS_CONTROL,
// que profesor también tiene): profesor entra a la sección pero no ve el
// lector. Fuente única de verdad para AccessControl/index.js y el ícono de
// acceso rápido del navbar (AdminNavbarLinks.js) — no repetir esta regla.
export const useCanOpenScanner = () => {
  const isAdmin = useRoleModuleAccess(MODULES.SISTEMA);
  const { user } = useUserAuth();
  return isAdmin || user?.role === 'inspector';
};
